import {useAuth} from "../../providers/AuthProvider";
import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {ArrowRight} from "lucide-react";
import {formatMinutes, getJourneyProgress, getOptionalSuggestions} from "./components/applicationProgress";
import JourneyModal from "./journey/JourneyModal";
import JourneyList, {JourneyActions, JourneyFoot, OkBox, sectionRows} from "./journey/JourneyList";

const PROFILE_REVIEW = '1–2 business days';

/**
 * Profile "Submit for approval" pop-up, opened by the ?submitForApproval param.
 *  not ready  – what's still required
 *  ready      – confirm, with what happens next
 *  submitted  – confirmation plus optional sections worth adding while Wripple reviews
 */
const ReadyToSubmitProfileModal = ({ onSubmitProfileApproval }) => {

    const {auth, submitProfileForApproval, isUpdating} = useAuth();
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();
    const [submitted, setSubmitted] = useState(false);

    const isOpen = searchParams?.has('submitForApproval');
    const isReady = !!auth?.isProfileReadyForApproval?.();

    // Reset once the param is gone (closed, or navigated to another section)
    useEffect(() => {
        if (!isOpen) setSubmitted(false);
    }, [isOpen]);

    const onClose = () => {
        if (isUpdating) {
            return;
        }
        let params = new URLSearchParams(searchParams);
        params.delete('submitForApproval');
        setSearchParams(params);
    }

    const onSubmit = async () => {
        const response = await submitProfileForApproval();

        if (response) {
            setSubmitted(true);
            await onSubmitProfileApproval(response)
        }
    }

    const progress = useMemo(
        () => (isOpen && auth ? getJourneyProgress(auth, [...(auth.getFullProfileStepsForTalent?.() || [])]) : null),
        [auth, isOpen]
    );
    const optional = useMemo(() => (submitted ? getOptionalSuggestions(auth) : null), [auth, submitted]);

    // One modal whose content switches, so ready -> submitted doesn't re-animate
    let view;

    if (submitted) {
        const suggestion = optional?.remaining?.[0];
        const isVideo = suggestion?.path === '/video-interview';
        view = {
            icon: 'check',
            title: 'Profile submitted for approval',
            subtitle: `We'll email you within ${PROFILE_REVIEW}`,
            body: (
                <>
                    <JourneyList
                        title="Worth adding while you wait"
                        meta="changes are included in the review"
                        rows={sectionRows(optional?.remaining, {badgeFirst: isVideo ? 'Recommended' : undefined})}
                    />
                    <JourneyActions>
                        <button type="button" className="btn btn-outline-dark px-6" onClick={() => navigate('/talent/dashboard')}>
                            Go to dashboard
                        </button>
                        {suggestion && (
                            <button type="button" className="btn btn-primary inline-flex items-center justify-center gap-2 px-6"
                                    onClick={() => navigate(`/talent/application${suggestion.path}`)}>
                                {isVideo ? 'Start your video interview' : `Add ${suggestion.title}`} <ArrowRight className="h-4 w-4" />
                            </button>
                        )}
                    </JourneyActions>
                </>
            ),
        };
    } else if (!isReady) {
        const remaining = progress?.requiredRemaining || [];
        view = {
            icon: 'sparkles',
            title: 'Almost there',
            subtitle: `${remaining.length} required section${remaining.length === 1 ? '' : 's'} left before you can submit`,
            body: (
                <>
                    <JourneyList
                        title="Still to do"
                        meta={progress?.minutesLeft ? `~${formatMinutes(progress.minutesLeft)}` : null}
                        rows={sectionRows(remaining)}
                    />
                    <JourneyActions>
                        <button type="button" className="btn btn-primary px-8" onClick={onClose}>Keep editing</button>
                    </JourneyActions>
                </>
            ),
        };
    } else {
        view = {
            icon: 'submit',
            title: "You're ready to submit!",
            subtitle: 'One last step before clients can see you',
            body: (
                <>
                    <OkBox>All {progress?.requiredCount || ''} required sections complete</OkBox>
                    <JourneyList
                        title="What happens next"
                        rows={[
                            { kind: 'stepnow', label: 'Submit for approval', meta: 'now' },
                            { kind: 'step', label: 'Wripple reviews your profile', meta: PROFILE_REVIEW },
                            { kind: 'step', label: 'Go live to clients' },
                        ]}
                    />
                    <JourneyActions>
                        <button type="button" disabled={isUpdating} onClick={onClose} className="btn btn-outline-dark px-6">
                            Keep editing
                        </button>
                        <button type="button" disabled={isUpdating} onClick={onSubmit}
                                className="btn btn-primary inline-flex items-center justify-center gap-2 px-6">
                            {isUpdating ? 'Submitting...' : <>Submit for approval <ArrowRight className="h-4 w-4" /></>}
                        </button>
                    </JourneyActions>
                    <JourneyFoot>Optional sections can be added now or while we review.</JourneyFoot>
                </>
            ),
        };
    }

    return (
        <JourneyModal open={isOpen} onClose={onClose} busy={isUpdating}
                      icon={view.icon} title={view.title} subtitle={view.subtitle}>
            {view.body}
        </JourneyModal>
    );
};


export default ReadyToSubmitProfileModal
